import { useState, useEffect, useMemo, useRef } from "react";
import { BarChart3, Download, ExternalLink, Filter, X, ArrowUp, ArrowDown, Search, FileText, Mail, Building2, ChevronRight, RefreshCw } from "lucide-react";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { readCache, writeCache, isFresh } from "@/lib/offlineCache";

const KpiCard = ({ label, sub, value, valueClass, title, testId, pct }) => (
  <div className="dolphin-card px-3 py-3 sm:px-4 sm:py-4 min-w-0" data-testid={testId}>
    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap truncate" title={label}>{label}</p>
    <p className="text-[10px] text-slate-400 h-3.5 leading-3.5">{sub}</p>
    <div className="flex items-baseline gap-1.5 mt-1 min-w-0">
      <p className={`text-xl sm:text-2xl font-bold whitespace-nowrap tabular-nums ${valueClass}`} title={title}>{value}</p>
      {pct != null && (
        <span className={`text-xs font-semibold whitespace-nowrap tabular-nums ${valueClass} opacity-80`} data-testid={`${testId}-pct`}>
          {pct}%
        </span>
      )}
    </div>
  </div>
);

const pctOf = (part, total) => (total > 0 ? ((part / total) * 100).toFixed(1) : "0.0");

const formatAgo = (ts) => {
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
};
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line
} from "recharts";
import OfflineMap from "@/components/OfflineMap";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";

const COLORS = ['#006994', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'];

export default function OfflineAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]); 
  const [onlineData, setOnlineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: "",
    dma: [],
    pa: [],
    fa: [],
    city: "",
    revenueSort: "",
    offlineDaysMin: "2",
    offlineDaysMax: "",
    ddCodeSearch: "",
    mongoDma: [],
    stickSeries: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [citySearch, setCitySearch] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const chartsRef = useRef(null);
  
  const [deviceType, setDeviceType] = useState("NON API");
  const pageSize = 20;
  const [ddCodeSearchInput, setDdCodeSearchInput] = useState("");
  const [ddCodeSuggestions, setDdCodeSuggestions] = useState([]);
  const [showDdCodeSuggestions, setShowDdCodeSuggestions] = useState(false);
  const [paSearchQuery, setPaSearchQuery] = useState("");
  const [paPopoverOpen, setPaPopoverOpen] = useState(false);
  const [stateSearchQuery, setStateSearchQuery] = useState("");
  const [statePopoverOpen, setStatePopoverOpen] = useState(false);
  const [faSearchQuery, setFaSearchQuery] = useState("");
  const [faPopoverOpen, setFaPopoverOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [includePdf, setIncludePdf] = useState(true);
  const [includeCsv, setIncludeCsv] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  const [availableDates, setAvailableDates] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const autoDateRef = useRef("");
  const forceRefreshRef = useRef(false);
  const userId = user?.user_id;

  const applyDates = (dates) => {
    setAvailableDates(dates);
    if (dates.length === 0) return;
    // Follow the newest date unless the user picked a different one
    setFilters(prev => (!prev.date || prev.date === autoDateRef.current) ? { ...prev, date: dates[0] } : prev);
    autoDateRef.current = dates[0];
  };

  const applyPayload = (payload) => {
    const rows = payload?.data || [];
    setAllData(rows);
    setData(rows.filter(row => row.Status === "Offline"));
    setOnlineData(payload?.online_data || []);
  };

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const cached = await readCache(userId, "dates");
      if (cancelled) return;
      if (cached) applyDates(cached.value);
      if (isFresh(cached)) return;
      try {
        const res = await axios.get(`${API}/offline-analytics/dates`, { withCredentials: true });
        if (cancelled) return;
        const dates = res.data?.dates || [];
        applyDates(dates);
        writeCache(userId, "dates", dates);
      } catch (err) {
        console.error("Error loading offline analytics dates:", err);
        if (!cached) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!filters.date || !userId) return;
    let cancelled = false;
    const force = forceRefreshRef.current;
    forceRefreshRef.current = false;
    (async () => {
      const cacheName = `data:${filters.date}`;
      const cached = force ? null : await readCache(userId, cacheName);
      if (cancelled) return;
      if (cached) {
        applyPayload(cached.value);
        setLastUpdated(cached.savedAt);
        setLoading(false);
        if (isFresh(cached)) return;
      } else if (!force) {
        setLoading(true);
      }
      try {
        const res = await axios.get(`${API}/offline-analytics`, {
          params: { date: filters.date, ...(force ? { refresh: true } : {}) },
          withCredentials: true,
        });
        if (cancelled) return;
        applyPayload(res.data);
        setLastUpdated(Date.now());
        writeCache(userId, cacheName, res.data);
        if (force) toast.success("Offline Analytics refreshed");
      } catch (err) {
        console.error("Error loading offline analytics data:", err);
        if (force) toast.error("Could not refresh. Showing the saved copy.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [filters.date, userId, refreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/offline-analytics/dates`, { params: { refresh: true }, withCredentials: true });
      const dates = res.data?.dates || [];
      applyDates(dates);
      writeCache(userId, "dates", dates);
    } catch (err) {
      console.error("Error refreshing dates:", err);
    }
    forceRefreshRef.current = true;
    setRefreshNonce(n => n + 1);
  };


  const filterOptions = useMemo(() => {
    const dates = availableDates;
    const dmas = [...new Set(data.map(d => d.State).filter(Boolean))].sort();
    const pas = [...new Set(data.map(d => d["PA Title"]).filter(Boolean))].sort();
    const fas = [...new Set(data.map(d => d["FA Title"]).filter(Boolean))].sort();
    const cities = [...new Set(data.map(d => d.City).filter(Boolean))].sort();
    const mongoDmas = [...new Set(data.map(d => d.DMA).filter(Boolean))].sort();
    const stickSeriesList = [...new Set(data.map(d => d["Stick Series"]).filter(Boolean))].sort();
    return { dates, dmas, pas, fas, cities, mongoDmas, stickSeriesList };
  }, [data, availableDates]);

  const ddCodeOptions = useMemo(() => {
    const uniqueMap = new Map();
    data.forEach(d => {
      const ddCode = d["Venue ID"];
      if (ddCode && !uniqueMap.has(ddCode)) {
        uniqueMap.set(ddCode, {
          dd_code: ddCode,
          store_name: d["Store Name"] || "",
          address: d["Address"] || "",
          city: d["City"] || "",
          state: d["State"] || ""
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [data]);

  // Search DD Codes with autocomplete
  useEffect(() => {
    if (ddCodeSearchInput.length < 1) {
      setDdCodeSuggestions([]);
      return;
    }
    
    const searchTerm = ddCodeSearchInput.toLowerCase();
    const filtered = ddCodeOptions.filter(item => 
      item.dd_code.toLowerCase().includes(searchTerm) ||
      item.store_name.toLowerCase().includes(searchTerm) ||
      item.address.toLowerCase().includes(searchTerm)
    ).slice(0, 10);
    
    setDdCodeSuggestions(filtered);
  }, [ddCodeSearchInput, ddCodeOptions]);


  const handleSelectDdCode = (item) => {
    setDdCodeSearchInput(item.dd_code);
    setFilters(prev => ({ ...prev, ddCodeSearch: item.dd_code }));
    setShowDdCodeSuggestions(false);
    setDdCodeSuggestions([]);
  };

  const clearDdCodeSearch = () => {
    setDdCodeSearchInput("");
    setFilters(prev => ({ ...prev, ddCodeSearch: "" }));
    setDdCodeSuggestions([]);
    setShowDdCodeSuggestions(false);
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return new Date(parts[2], parts[0] - 1, parts[1]);
    }
    return new Date(dateStr);
  };

  const mostRecentDateStr = useMemo(() => availableDates[0] || null, [availableDates]);
  const filteredData = useMemo(() => {
    const targetDate = filters.date || mostRecentDateStr;
    let result = data.filter(d => d.Date === targetDate);
    result = result.filter(d => d["Device Type"] === deviceType);
    if (filters.ddCodeSearch && filters.ddCodeSearch.trim()) {
      const searchTerm = filters.ddCodeSearch.toLowerCase().trim();
      result = result.filter(d => {
        const ddCode = (d["Venue ID"] || "").toLowerCase();
        const storeName = (d["Store Name"] || "").toLowerCase();
        const venueName = (d["Venue Name"] || "").toLowerCase();
        return ddCode.includes(searchTerm) || storeName.includes(searchTerm) || venueName.includes(searchTerm);
      });
    }
    
    if (filters.dma && filters.dma.length > 0) {
      result = result.filter(d => filters.dma.includes(d.State));
    }
    if (filters.pa && filters.pa.length > 0) {
      result = result.filter(d => filters.pa.includes(d["PA Title"]));
    }
    if (filters.fa && filters.fa.length > 0) {
      result = result.filter(d => filters.fa.includes(d["FA Title"]));
    }
    if (filters.city) {
      result = result.filter(d => d.City === filters.city);
    }
    if (filters.mongoDma && filters.mongoDma.length > 0) {
      result = result.filter(d => filters.mongoDma.includes(d.DMA));
    }
    if (filters.stickSeries && filters.stickSeries.length > 0) {
      result = result.filter(d => filters.stickSeries.includes(d["Stick Series"]));
    }
    
    const minDays = filters.offlineDaysMin !== "" ? parseInt(filters.offlineDaysMin, 10) : 2;
    result = result.filter(d => {
      const days = parseInt(d["Offline Days"] || 0, 10);
      return days >= minDays;
    });
    
    if (filters.offlineDaysMax !== "") {
      const maxDays = parseInt(filters.offlineDaysMax, 10);
      result = result.filter(d => {
        const days = parseInt(d["Offline Days"] || 0, 10);
        return days <= maxDays;
      });
    }
    
    if (filters.revenueSort === "asc") {
      result.sort((a, b) => parseFloat(a["Last 2 Month Revenue"] || 0) - parseFloat(b["Last 2 Month Revenue"] || 0));
    } else if (filters.revenueSort === "desc") {
      result.sort((a, b) => parseFloat(b["Last 2 Month Revenue"] || 0) - parseFloat(a["Last 2 Month Revenue"] || 0));
    } else {
      result.sort((a, b) => {
        return parseDate(b.Date) - parseDate(a.Date);
      });
    }
    
    return result;
  }, [data, filters, mostRecentDateStr, deviceType]);
  const hasActiveFilters = (filters.date && filters.date !== mostRecentDateStr) || (filters.dma && filters.dma.length > 0) || (filters.pa && filters.pa.length > 0) || filters.city || filters.revenueSort || (filters.offlineDaysMin && filters.offlineDaysMin !== "2") || filters.offlineDaysMax || filters.ddCodeSearch || (filters.mongoDma && filters.mongoDma.length > 0) || (filters.stickSeries && filters.stickSeries.length > 0);

  const analyticsData = filteredData;
  
  // Filter online data with the same filters as offline
  const filteredOnlineCount = useMemo(() => {
    let result = [...onlineData];
    
    // Device type filter
    if (deviceType) {
      result = result.filter(d => d["Device Type"] === deviceType);
    }
    // State filter
    if (filters.dma && filters.dma.length > 0) {
      result = result.filter(d => filters.dma.includes(d.State));
    }
    // PA filter
    if (filters.pa && filters.pa.length > 0) {
      result = result.filter(d => filters.pa.includes(d["PA Title"]));
    }
    // FA filter
    if (filters.fa && filters.fa.length > 0) {
      result = result.filter(d => filters.fa.includes(d["FA Title"]));
    }
    // City filter
    if (filters.city) {
      result = result.filter(d => d.City === filters.city);
    }
    // DMA filter
    if (filters.mongoDma && filters.mongoDma.length > 0) {
      result = result.filter(d => filters.mongoDma.includes(d.DMA));
    }
    // Stick Series filter
    if (filters.stickSeries && filters.stickSeries.length > 0) {
      result = result.filter(d => filters.stickSeries.includes(d["Stick Series"]));
    }
    // DD Code / Store Name search
    if (filters.ddCodeSearch) {
      const q = filters.ddCodeSearch.toLowerCase();
      result = result.filter(d => 
        (d["Venue ID"] || "").toLowerCase().includes(q) || 
        (d["Store Name"] || "").toLowerCase().includes(q)
      );
    }
    return result.length;
  }, [onlineData, filters, deviceType]);

  const statsData = useMemo(() => {
    // Calculate revenue
    const totalRevenue = analyticsData.reduce((sum, d) => sum + parseFloat(d["Last 2 Month Revenue"] || 0), 0);
    
    // Unique states and cities
    const uniqueStates = new Set(analyticsData.map(d => d.State).filter(Boolean));
    const uniqueCities = new Set(analyticsData.map(d => d.City).filter(Boolean));

    return {
      totalScreens: analyticsData.length + filteredOnlineCount,
      totalOffline: analyticsData.length,
      onlineScreens: filteredOnlineCount,
      statesAffected: uniqueStates.size,
      citiesAffected: uniqueCities.size,
      totalRevenue: totalRevenue,
      selectedDate: filters.date || mostRecentDateStr,
      showingFiltered: hasActiveFilters
    };
  }, [analyticsData, filteredOnlineCount, hasActiveFilters, mostRecentDateStr, filters.date]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return analyticsData.slice(start, start + pageSize);
  }, [analyticsData, currentPage]);

  const totalPages = Math.ceil(analyticsData.length / pageSize);
  const totalRecords = analyticsData.length;


  const chartData = useMemo(() => {
    const stateCounts = {};
    analyticsData.forEach(d => {
      const state = d.State || "Unknown";
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    });
    const byState = Object.entries(stateCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // By City (Top 10)
    const cityCounts = {};
    analyticsData.forEach(d => {
      const city = d.City || "Unknown";
      cityCounts[city] = (cityCounts[city] || 0) + 1;
    });
    const byCity = Object.entries(cityCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const paCounts = {};
    analyticsData.forEach(d => {
      const pa = d["PA Title"] || "Unknown";
      paCounts[pa] = (paCounts[pa] || 0) + 1;
    });
    const byPA = Object.entries(paCounts)
      .map(([name, value]) => ({ name: name.length > 20 ? name.substring(0, 20) + "..." : name, value, fullName: name }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // By Network Type
    const networkCounts = {};
    analyticsData.forEach(d => {
      const network = d["Network Type"] || "Unknown";
      networkCounts[network] = (networkCounts[network] || 0) + 1;
    });
    const byNetwork = Object.entries(networkCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const osCounts = {};
    analyticsData.forEach(d => {
      const os = d["OS Version"] || "Unknown";
      osCounts[os] = (osCounts[os] || 0) + 1;
    });
    const byOS = Object.entries(osCounts)
      .map(([name, value]) => ({ name: `v${name}`, value }))
      .sort((a, b) => b.value - a.value);

    return { byState, byCity, byPA, byNetwork, byOS };
  }, [analyticsData]);

  const [trendRaw, setTrendRaw] = useState([]);
  const [trendRange, setTrendRange] = useState("30");
  const [trendLoading, setTrendLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTrendLoading(true);
      try {
        const params = new URLSearchParams();
        if (trendRange !== "all") params.append('days', trendRange);
        if (deviceType) params.append('device_type', deviceType);
        (filters.dma || []).forEach(s => params.append('states', s));
        (filters.pa || []).forEach(p => params.append('pas', p));
        (filters.fa || []).forEach(f => params.append('fas', f));
        (filters.mongoDma || []).forEach(d => params.append('dmas', d));
        (filters.stickSeries || []).forEach(s => params.append('stick_series', s));
        if (filters.city) params.append('city', filters.city);
        const minDays = filters.offlineDaysMin !== "" ? parseInt(filters.offlineDaysMin, 10) : 2;
        if (!Number.isNaN(minDays)) params.append('offline_days_min', String(minDays));
        if (filters.offlineDaysMax !== "") {
          const maxDays = parseInt(filters.offlineDaysMax, 10);
          if (!Number.isNaN(maxDays)) params.append('offline_days_max', String(maxDays));
        }
        if (filters.ddCodeSearch) params.append('dd_search', filters.ddCodeSearch);

        const res = await axios.get(`${API}/offline-analytics/trend?${params.toString()}`, {
          withCredentials: true,
        });
        if (!cancelled) setTrendRaw(res.data?.trend || []);
      } catch (err) {
        console.error('Error loading offline trend:', err);
        if (!cancelled) setTrendRaw([]);
      } finally {
        if (!cancelled) setTrendLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [trendRange, deviceType, filters.dma, filters.pa, filters.fa, filters.mongoDma, filters.stickSeries, filters.city, filters.offlineDaysMin, filters.offlineDaysMax, filters.ddCodeSearch]);

  const trendData = useMemo(() => {
    if (!trendRaw || trendRaw.length === 0) return [];
    return trendRaw.map(item => ({
      date: (item.date || '').substring(0, 5), // MM-DD
      fullDate: item.date,
      count: item.count,
      newOffline: item.new_offline || 0,
      cameOnline: item.came_online || 0,
    }));
  }, [trendRaw]);

  const agingData = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) return [];
    
    const agingBuckets = {
      '0-2': { count: 0, label: '0-2 days', description: 'Newly offline' },
      '3-7': { count: 0, label: '3-7 days', description: 'Short-term' },
      '8-30': { count: 0, label: '8-30 days', description: 'Medium-term' },
      '31-90': { count: 0, label: '31-90 days', description: 'Long unresolved' },
      '91-180': { count: 0, label: '91-180 days', description: 'Critical' },
      '181-300': { count: 0, label: '181-300 days', description: 'Very old' },
      '300+': { count: 0, label: '300+ days', description: 'Abandoned' },
    };
    
    analyticsData.forEach(d => {
      const days = parseInt(d["Offline Days"]) || 0;
      if (days <= 2) agingBuckets['0-2'].count++;
      else if (days <= 7) agingBuckets['3-7'].count++;
      else if (days <= 30) agingBuckets['8-30'].count++;
      else if (days <= 90) agingBuckets['31-90'].count++;
      else if (days <= 180) agingBuckets['91-180'].count++;
      else if (days <= 300) agingBuckets['181-300'].count++;
      else agingBuckets['300+'].count++;
    });
    
    return Object.entries(agingBuckets).map(([key, val]) => ({
      name: val.label,
      count: val.count,
      description: val.description,
    }));
  }, [analyticsData]);

  const osVersionSummary = useMemo(() => {
    if (!allData || allData.length === 0 || !filters.date) return [];
    
    // Filter data by selected date and device type
    let dateData = allData.filter(d => d.Date === filters.date);
    if (deviceType) {
      dateData = dateData.filter(d => d["Device Type"] === deviceType);
    }
    
    if (filters.dma && filters.dma.length > 0) {
      dateData = dateData.filter(d => filters.dma.includes(d.State));
    }
    
    if (filters.pa && filters.pa.length > 0) {
      dateData = dateData.filter(d => filters.pa.includes(d["PA Title"]));
    }
    
    if (filters.fa && filters.fa.length > 0) {
      dateData = dateData.filter(d => filters.fa.includes(d["FA Title"]));
    }
    
    // Filter by city
    if (filters.city) {
      dateData = dateData.filter(d => d.City === filters.city);
    }
    
    if (filters.ddCodeSearch) {
      const search = filters.ddCodeSearch.toLowerCase();
      dateData = dateData.filter(d => 
        (d["Venue ID"] || "").toLowerCase().includes(search) ||
        (d["Store Name"] || "").toLowerCase().includes(search) ||
        (d.Address || "").toLowerCase().includes(search)
      );
    }
    const minDays = filters.offlineDaysMin !== "" ? parseInt(filters.offlineDaysMin, 10) : 2;
    const maxDays = filters.offlineDaysMax !== "" ? parseInt(filters.offlineDaysMax, 10) : null;
    
    // Group by OS Version
    const osStats = {};
    dateData.forEach(d => {
      const os = d["OS Version"] || "Unknown";
      const status = d.Status;
      const offlineDays = parseInt(d["Offline Days"]) || 0;
      
      if (status === "Offline") {
        if (offlineDays < minDays) return; 
        if (maxDays !== null && offlineDays > maxDays) return; 
      }
      
      if (!osStats[os]) {
        osStats[os] = { os, total: 0, online: 0, offline: 0 };
      }
      osStats[os].total++;
      if (status === "Online") {
        osStats[os].online++;
      } else if (status === "Offline") {
        osStats[os].offline++;
      }
    });
    
    return Object.values(osStats)
      .map(item => ({
        ...item,
        offlinePercent: item.total > 0 ? ((item.offline / item.total) * 100).toFixed(1) : "0.0"
      }))
      .sort((a, b) => b.offline - a.offline);
  }, [allData, filters.date, filters.dma, filters.pa, filters.fa, filters.city, filters.ddCodeSearch, filters.offlineDaysMin, filters.offlineDaysMax, deviceType]);

  const handleFilterChange = (field, value) => {
    if (Array.isArray(value)) {
      setFilters(prev => ({ ...prev, [field]: value }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value === "all" ? "" : value }));
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ date: mostRecentDateStr, dma: [], pa: [], fa: [], city: "", revenueSort: "", offlineDaysMin: "2", offlineDaysMax: "", ddCodeSearch: "", mongoDma: [], stickSeries: [] });
    setDdCodeSearchInput("");
    setDdCodeSuggestions([]);
    setPaSearchQuery("");
    setFaSearchQuery("");
    setStateSearchQuery("");
    setCitySearch("");
    setShowCityDropdown(false);
    setCurrentPage(1);
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/logs/recent-comments`, { withCredentials: true });
      const commentsMap = response.data.comments || {};
      const dataWithComments = analyticsData.map(row => {
        const ddCode = row["Venue ID"];
        const commentData = commentsMap[ddCode] || {};
        return {
          ...row,
          "Most Recent Log Date": commentData.date || "",
          "Most Recent Category": commentData.category || "",
          "Most Recent Comment": commentData.comment || ""
        };
      });
      
      const csv = Papa.unparse(dataWithComments);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `offline_analytics_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error("Error fetching comments:", error);
      const csv = Papa.unparse(analyticsData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `offline_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      toast.error("Could not fetch recent comments. Downloaded without comments.");
    }
  };

  const handlePdfDownload = async () => {
    setGeneratingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Helper function to add text
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = [0, 0, 0] } = options;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);
        pdf.text(text, x, y);
      };

      // Title
      addText('Dolphin Venue Vision - Offline Analytics Report', margin, yPos, { fontSize: 18, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 10;
      addText(`Generated: ${new Date().toLocaleString()}`, margin, yPos, { fontSize: 9, color: [100, 100, 100] });
      yPos += 8;
      
      let filterInfo = hasActiveFilters ? 'Filtered Data Report' : `Most Recent Date: ${mostRecentDateStr || 'N/A'}`;
      filterInfo += ` | Device Type: ${deviceType}`;
      if (filters.pa && filters.pa.length > 0) {
        filterInfo += ` | PA: ${filters.pa.join(', ')}`;
      }
      if (filters.dma && filters.dma.length > 0) {
        filterInfo += ` | State: ${filters.dma.join(', ')}`;
      }
      if (filters.city) {
        filterInfo += ` | City: ${filters.city}`;
      }
      addText(filterInfo, margin, yPos, { fontSize: 10, color: [80, 80, 80] });
      yPos += 12;

      pdf.setDrawColor(0, 105, 148);
      pdf.setFillColor(240, 248, 255);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');
      yPos += 8;
      addText('Summary Statistics', margin + 5, yPos, { fontSize: 12, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 8;
      
      const statsLine1 = `Total Offline Screens: ${statsData.totalOffline.toLocaleString()}     |     States Affected: ${statsData.statesAffected}     |     Cities Affected: ${statsData.citiesAffected}`;
      addText(statsLine1, margin + 5, yPos, { fontSize: 10 });
      yPos += 7;
      
      const statsLine2 = `Revenue Impact (Last 2 Months): $${statsData.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      addText(statsLine2, margin + 5, yPos, { fontSize: 10 });
      yPos += 18;

      // Top 10 by State
      addText('Top 10 States by Offline Screens', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 6;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      chartData.byState.forEach((item, index) => {
        addText(`${index + 1}. ${item.name}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
        yPos += 5;
      });
      yPos += 8;

      // Top 10 by City
      addText('Top 10 Cities by Offline Screens', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 6;
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      chartData.byCity.forEach((item, index) => {
        addText(`${index + 1}. ${item.name}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
        yPos += 5;
      });
      yPos += 8;

      // Top 10 by PA
      addText('Top 10 PA Titles by Offline Screens', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 6;
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      chartData.byPA.forEach((item, index) => {
        const paName = item.fullName || item.name;
        addText(`${index + 1}. ${paName}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
        yPos += 5;
      });
      yPos += 8;

      if (yPos > pageHeight - 60) {
        pdf.addPage();
        yPos = margin;
      }
      addText('Network Type Distribution', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 6;
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      chartData.byNetwork.forEach((item) => {
        addText(`${item.name}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
        yPos += 5;
      });
      yPos += 8;
      addText('OS Version Distribution', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 6;
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      chartData.byOS.slice(0, 10).forEach((item) => {
        addText(`${item.name}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
        yPos += 5;
      });

      if (chartsRef.current) {
        pdf.addPage();
        yPos = margin;
        addText('Visual Charts', margin, yPos, { fontSize: 14, fontStyle: 'bold', color: [0, 105, 148] });
        yPos += 10;

        try {
          const canvas = await html2canvas(chartsRef.current, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Check if image fits on current page
          if (yPos + imgHeight > pageHeight - margin) {
            const scaleFactor = (pageHeight - margin - yPos) / imgHeight;
            pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth * scaleFactor, imgHeight * scaleFactor);
          } else {
            pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, imgHeight);
          }
        } catch (err) {
          console.error('Error capturing charts:', err);
          addText('Charts could not be captured. Please refer to the web interface.', margin, yPos, { fontSize: 10, color: [150, 0, 0] });
        }
      }

      pdf.addPage();
      yPos = margin;
      addText(`Data Table (showing top ${Math.min(50, analyticsData.length)} of ${analyticsData.length} records)`, margin, yPos, { fontSize: 12, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 8;
      const headers = ['DD Code', 'Store', 'Address', 'City', 'State', 'PA', 'FA', 'Days', 'Revenue'];
      const colWidths = [22, 26, 34, 18, 16, 22, 22, 12, 16];
      
      pdf.setFillColor(0, 105, 148);
      pdf.rect(margin, yPos, pageWidth - 2 * margin, 7, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      
      let xPos = margin + 2;
      headers.forEach((header, i) => {
        pdf.text(header, xPos, yPos + 5);
        xPos += colWidths[i];
      });
      yPos += 9;

      // Table rows
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6);
      
      analyticsData.slice(0, 50).forEach((row, index) => {
        if (yPos > pageHeight - 15) {
          pdf.addPage();
          yPos = margin;
        }
        
        // Alternate row colors
        if (index % 2 === 0) {
          pdf.setFillColor(248, 250, 252);
          pdf.rect(margin, yPos - 3, pageWidth - 2 * margin, 6, 'F');
        }
        
        xPos = margin + 2;
        const rowData = [
          (row["Venue ID"] || '').substring(0, 11),
          (row["Store Name"] || '').substring(0, 12),
          (row["Address"] || '').substring(0, 16),
          (row["City"] || '').substring(0, 8),
          (row["State"] || '').substring(0, 8),
          (row["PA Title"] || '').substring(0, 10),
          (row["FA Title"] || '').substring(0, 10),
          parseFloat(row["Offline Days"] || 0).toFixed(0),
          '$' + parseFloat(row["Last 2 Month Revenue"] || 0).toFixed(2)
        ];
        
        rowData.forEach((cell, i) => {
          pdf.text(String(cell), xPos, yPos);
          xPos += colWidths[i];
        });
        yPos += 6;
      });

      const totalPdfPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPdfPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPdfPages}`, pageWidth - 25, pageHeight - 8);
        pdf.text('Dolphin Digital OOH Media', margin, pageHeight - 8);
      }

      pdf.save(`dolphin_offline_analytics_report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const generatePdfBase64 = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    const addText = (text, x, y, options = {}) => {
      const { fontSize = 10, fontStyle = 'normal', color = [0, 0, 0] } = options;
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', fontStyle);
      pdf.setTextColor(...color);
      pdf.text(text, x, y);
    };

    // ===== PAGE 1 =====
    // Title
    addText('Dolphin Venue Vision - Offline Analytics Report', margin, yPos, { fontSize: 16, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 8;
    addText(`Generated: ${new Date().toLocaleString()}`, margin, yPos, { fontSize: 9, color: [100, 100, 100] });
    yPos += 6;
    
    let filterInfo = getFilterSummary();
    addText(`Filtered Data Report | ${filterInfo}`, margin, yPos, { fontSize: 9, color: [80, 80, 80] });
    yPos += 10;

    pdf.setFillColor(240, 248, 255);
    pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 28, 3, 3, 'F');
    yPos += 8;
    addText('Summary Statistics', margin + 5, yPos, { fontSize: 12, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 8;
    
    addText(`Total Offline Screens: ${statsData.totalOffline.toLocaleString()}`, margin + 5, yPos, { fontSize: 10 });
    addText(`States Affected: ${statsData.statesAffected}`, pageWidth / 2, yPos, { fontSize: 10 });
    yPos += 6;
    addText(`Cities Affected: ${statsData.citiesAffected}`, margin + 5, yPos, { fontSize: 10 });
    addText(`Revenue Impact (Last 2 Months): $${statsData.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth / 2, yPos, { fontSize: 10, color: [220, 38, 38] });
    yPos += 14;

    // Top 10 States
    addText('Top 10 States by Offline Screens', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 2;
    pdf.setDrawColor(0, 105, 148);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
    
    chartData.byState.slice(0, 10).forEach((item, index) => {
      const barWidth = Math.min((item.value / (chartData.byState[0]?.value || 1)) * 50, 50);
      pdf.setFillColor(0, 105, 148);
      pdf.rect(margin + 40, yPos - 3, barWidth, 4, 'F');
      addText(`${index + 1}. ${item.name}`, margin, yPos, { fontSize: 9 });
      addText(`${item.value}`, margin + 95, yPos, { fontSize: 9, fontStyle: 'bold' });
      yPos += 5;
    });
    yPos += 6;

    // Top 10 Cities
    addText('Top 10 Cities by Offline Screens', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 2;
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
    
    chartData.byCity.slice(0, 10).forEach((item, index) => {
      const barWidth = Math.min((item.value / (chartData.byCity[0]?.value || 1)) * 50, 50);
      pdf.setFillColor(14, 165, 233);
      pdf.rect(margin + 45, yPos - 3, barWidth, 4, 'F');
      addText(`${index + 1}. ${item.name.substring(0, 18)}`, margin, yPos, { fontSize: 9 });
      addText(`${item.value}`, margin + 100, yPos, { fontSize: 9, fontStyle: 'bold' });
      yPos += 5;
    });
    yPos += 6;

    // Top 10 PAs
    addText('Top 10 PA Titles by Offline Screens', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 2;
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
    
    chartData.byPA.slice(0, 10).forEach((item, index) => {
      const barWidth = Math.min((item.value / (chartData.byPA[0]?.value || 1)) * 50, 50);
      pdf.setFillColor(34, 197, 94);
      pdf.rect(margin + 55, yPos - 3, barWidth, 4, 'F');
      addText(`${index + 1}. ${item.name.substring(0, 25)}`, margin, yPos, { fontSize: 9 });
      addText(`${item.value}`, margin + 110, yPos, { fontSize: 9, fontStyle: 'bold' });
      yPos += 5;
    });
    yPos += 6;


    addText('Network Type Distribution', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 2;
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
    
    chartData.byNetwork.forEach((item) => {
      addText(`${item.name}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
      yPos += 5;
    });
    yPos += 4;

    addText('OS Version Distribution', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 2;
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
    
    chartData.byOS.forEach((item) => {
      addText(`${item.name}: ${item.value} screens`, margin + 3, yPos, { fontSize: 9 });
      yPos += 5;
    });

    // ===== PAGE 2 =====
    if (chartsRef.current) {
      pdf.addPage();
      yPos = margin;
      addText('Visual Charts', margin, yPos, { fontSize: 14, fontStyle: 'bold', color: [0, 105, 148] });
      yPos += 10;

      try {
        const canvas = await html2canvas(chartsRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        if (yPos + imgHeight > pageHeight - margin) {
          const scaleFactor = (pageHeight - margin - yPos) / imgHeight;
          pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth * scaleFactor, imgHeight * scaleFactor);
        } else {
          pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, imgHeight);
        }
      } catch (err) {
        console.error('Error capturing charts:', err);
        addText('Charts could not be captured.', margin, yPos, { fontSize: 10, color: [150, 0, 0] });
      }
    }

    pdf.addPage();
    yPos = margin;
    addText(`Data Table (showing top ${Math.min(50, analyticsData.length)} of ${analyticsData.length} records)`, margin, yPos, { fontSize: 12, fontStyle: 'bold', color: [0, 105, 148] });
    yPos += 8;

    // Table headers with Address, PA and FA
    const headers = ['DD Code', 'Store', 'Address', 'City', 'State', 'PA', 'FA', 'Days', 'Revenue'];
    const colWidths = [22, 26, 34, 18, 16, 20, 20, 12, 16];
    
    pdf.setFillColor(0, 105, 148);
    pdf.rect(margin, yPos, pageWidth - 2 * margin, 7, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    
    let xPos = margin + 2;
    headers.forEach((header, i) => {
      pdf.text(header, xPos, yPos + 5);
      xPos += colWidths[i];
    });
    yPos += 9;

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6);
    
    analyticsData.slice(0, 50).forEach((row, index) => {
      if (yPos > pageHeight - 15) {
        pdf.addPage();
        yPos = margin;
        pdf.setFillColor(0, 105, 148);
        pdf.rect(margin, yPos, pageWidth - 2 * margin, 7, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        xPos = margin + 2;
        headers.forEach((header, i) => {
          pdf.text(header, xPos, yPos + 5);
          xPos += colWidths[i];
        });
        yPos += 9;
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
      }
      
      if (index % 2 === 0) {
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin, yPos - 3, pageWidth - 2 * margin, 6, 'F');
      }
      
      xPos = margin + 2;
      const rowData = [
        (row["Venue ID"] || '').substring(0, 11),
        (row["Store Name"] || '').substring(0, 12),
        (row["Address"] || '').substring(0, 16),
        (row["City"] || '').substring(0, 8),
        (row["State"] || '').substring(0, 8),
        (row["PA Title"] || '').substring(0, 10),
        (row["FA Title"] || '').substring(0, 10),
        String(row["Offline Days"] || 0),
        `$${parseFloat(row["Last 2 Month Revenue"] || 0).toFixed(2)}`
      ];
      
      rowData.forEach((cell, i) => {
        pdf.text(cell, xPos, yPos);
        xPos += colWidths[i];
      });
      yPos += 5;
    });

    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 8);
      pdf.text('Dolphin Digital OOH Media', margin, pageHeight - 8);
    }

    return pdf.output('datauristring').split(',')[1];
  };

  const getFilterSummary = () => {
    const parts = [];
    parts.push(`Device Type: ${deviceType}`);
    if (filters.date) parts.push(`Date: ${filters.date}`);
    if (filters.dma && filters.dma.length > 0) parts.push(`State: ${filters.dma.join(', ')}`);
    if (filters.pa && filters.pa.length > 0) parts.push(`PA: ${filters.pa.join(', ')}`);
    if (filters.fa && filters.fa.length > 0) parts.push(`FA: ${filters.fa.join(', ')}`);
    if (filters.city) parts.push(`City: ${filters.city}`);
    if (filters.offlineDaysMin || filters.offlineDaysMax) {
      parts.push(`Offline Days: ${filters.offlineDaysMin || '2'} - ${filters.offlineDaysMax || '∞'}`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'Most Recent Date: ' + (mostRecentDateStr || 'N/A');
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error("Please enter a recipient email address");
      return;
    }
    if (!includePdf && !includeCsv) {
      toast.error("Please select at least one attachment type");
      return;
    }

    setSendingEmail(true);
    try {
      let pdfData = null;
      let csvData = null;


      if (includePdf) {
        pdfData = await generatePdfBase64();
      }

      if (includeCsv) {
        csvData = Papa.unparse(analyticsData);
      }

      const response = await axios.post(`${API}/send-report`, {
        recipient_email: recipientEmail,
        include_pdf: includePdf,
        include_csv: includeCsv,
        pdf_data: pdfData,
        csv_data: csvData,
        filter_summary: getFilterSummary(),
        report_date: filters.date || mostRecentDateStr || new Date().toISOString().split('T')[0]
      });

      toast.success(response.data.message || "Report sent successfully!");
      setEmailModalOpen(false);
      setRecipientEmail("");
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || "Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const openLogsForDD = (ddCode) => {
    window.open(`/logs?dd=${encodeURIComponent(ddCode)}`, '_blank');
  };

  const formatRevenue = (value) => {
    const num = parseFloat(value || 0);
    return `$${num.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full glass-effect border-b border-slate-100 px-4 sm:px-8 py-4 sm:py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 ocean-gradient rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-[#006994]" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 whitespace-nowrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Offline Analytics
              </h1>
              <p className="text-sm text-slate-500">
                {loading ? "Loading..." : hasActiveFilters 
                  ? `${statsData.totalOffline.toLocaleString()} offline screens (filtered)`
                  : `${statsData.totalOffline.toLocaleString()} offline screens on ${statsData.selectedDate || "selected date"}`}
              </p>
              {lastUpdated && (
                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400" data-testid="oa-last-updated">
                  <span title={new Date(lastUpdated).toLocaleString()}>Updated {formatAgo(lastUpdated)}</span>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    data-testid="oa-refresh-btn"
                    className="inline-flex items-center gap-1 text-[#006994] hover:underline disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {user?.user_type === "internal" && (
            <Button onClick={() => setEmailModalOpen(true)} className="dolphin-btn-secondary" disabled={loading} data-testid="email-report-btn" title="Email Report">
              <Mail className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Email Report</span>
            </Button>
            )}
            <Button onClick={handlePdfDownload} className="dolphin-btn-secondary" disabled={loading || generatingPdf} data-testid="download-pdf-btn" title="Download PDF">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{generatingPdf ? 'Generating...' : 'Download PDF'}</span>
            </Button>
            <Button onClick={handleDownload} className="dolphin-btn-primary" disabled={loading} data-testid="download-csv-btn" title="Download CSV">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Download CSV</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="p-8 lg:p-12">
        {/* Filters */}
        <div className="dolphin-card p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {hasActiveFilters && (
                <Badge className="bg-[#006994] text-white ml-2">Active</Badge>
              )}
            </div>
            <div className="flex items-center gap-4">
              {/* Device Type Toggle */}
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-lg">
                <span className={`text-sm font-medium transition-colors ${deviceType === "NON API" ? "text-[#006994]" : "text-slate-400"}`}>
                  NON API
                </span>
                <Switch
                  checked={deviceType === "API"}
                  onCheckedChange={(checked) => setDeviceType(checked ? "API" : "NON API")}
                  data-testid="device-type-toggle"
                />
                <span className={`text-sm font-medium transition-colors ${deviceType === "API" ? "text-[#006994]" : "text-slate-400"}`}>
                  API
                </span>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2 relative">
              <Label className="text-xs text-slate-500 mb-1 block">DD Code / Store Name</Label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search by DD Code, Store Name, or Address..."
                  value={ddCodeSearchInput}
                  onChange={(e) => {
                    setDdCodeSearchInput(e.target.value);
                    setShowDdCodeSuggestions(true);
                    setFilters(prev => ({ ...prev, ddCodeSearch: e.target.value }));
                  }}
                  onFocus={() => ddCodeSearchInput && setShowDdCodeSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowDdCodeSuggestions(false), 200)}
                  className="h-12 pl-12 text-base"
                  data-testid="dd-code-search"
                />
                {ddCodeSearchInput && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDdCodeSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {showDdCodeSuggestions && ddCodeSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 z-[100] max-h-80 overflow-auto">
                  {ddCodeSuggestions.map((item) => (
                    <div
                      key={item.dd_code}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectDdCode(item);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors flex items-center gap-3 cursor-pointer select-none"
                      data-testid={`dd-suggestion-${item.dd_code}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#006994]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">{item.dd_code}</div>
                        <div className="text-sm text-slate-500 truncate">{item.store_name}</div>
                        <div className="text-xs text-slate-400 truncate">{item.address} • {item.city}, {item.state}</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
              <Select value={filters.date || mostRecentDateStr || ""} onValueChange={(v) => handleFilterChange("date", v)}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select Date" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {filterOptions.dates.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">State</Label>
              <Popover open={statePopoverOpen} onOpenChange={setStatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="h-12 w-full justify-between font-normal"
                  >
                    {filters.dma && filters.dma.length > 0 
                      ? filters.dma.length === 1 
                        ? filters.dma[0]
                        : `${filters.dma.length} States selected`
                      : "All States"}
                    <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-0" align="start">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search States..."
                      value={stateSearchQuery}
                      onChange={(e) => setStateSearchQuery(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <ScrollArea className="h-[250px]">
                    <div className="p-2">
                      <div className="flex gap-2 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => handleFilterChange("dma", [...filterOptions.dmas])}
                        >
                          Select All
                        </Button>
                        {filters.dma && filters.dma.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex-1 text-xs text-slate-500"
                            onClick={() => handleFilterChange("dma", [])}
                          >
                            Clear ({filters.dma.length})
                          </Button>
                        )}
                      </div>
                      {filterOptions.dmas
                        .filter(state => state.toLowerCase().includes(stateSearchQuery.toLowerCase()))
                        .map(state => {
                          const isSelected = filters.dma?.includes(state);
                          return (
                            <div
                              key={state}
                              className="flex items-center space-x-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                              onClick={() => {
                                const newStates = isSelected
                                  ? filters.dma.filter(s => s !== state)
                                  : [...(filters.dma || []), state];
                                handleFilterChange("dma", newStates);
                              }}
                            >
                              <Checkbox checked={isSelected} />
                              <span className="text-sm">{state}</span>
                            </div>
                          );
                        })}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">PA</Label>
              <Popover open={paPopoverOpen} onOpenChange={setPaPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="h-12 w-full justify-between font-normal"
                  >
                    {filters.pa && filters.pa.length > 0 
                      ? filters.pa.length === 1 
                        ? filters.pa[0].substring(0, 15) + (filters.pa[0].length > 15 ? '...' : '')
                        : `${filters.pa.length} PAs selected`
                      : "All PAs"}
                    <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search PAs..."
                      value={paSearchQuery}
                      onChange={(e) => setPaSearchQuery(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <ScrollArea className="h-[250px]">
                    <div className="p-2">
                      <div className="flex gap-2 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => { setFilters(prev => ({ ...prev, pa: [...filterOptions.pas] })); setCurrentPage(1); }}
                        >
                          Select All
                        </Button>
                        {filters.pa && filters.pa.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex-1 text-xs text-slate-500"
                            onClick={() => { setFilters(prev => ({ ...prev, pa: [] })); setCurrentPage(1); }}
                          >
                            Clear ({filters.pa.length})
                          </Button>
                        )}
                      </div>
                      {filterOptions.pas
                        .filter(pa => pa.toLowerCase().includes(paSearchQuery.toLowerCase()))
                        .map((pa) => (
                          <div
                            key={pa}
                            className="flex items-center space-x-2 py-1.5 px-2 hover:bg-slate-100 rounded cursor-pointer"
                            onClick={() => {
                              const newPas = filters.pa.includes(pa)
                                ? filters.pa.filter(p => p !== pa)
                                : [...filters.pa, pa];
                              setFilters(prev => ({ ...prev, pa: newPas }));
                              setCurrentPage(1);
                            }}
                          >
                            <Checkbox
                              checked={filters.pa.includes(pa)}
                              className="data-[state=checked]:bg-[#006994] data-[state=checked]:border-[#006994]"
                            />
                            <span className="text-sm truncate flex-1">{pa}</span>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">FA</Label>
              <Popover open={faPopoverOpen} onOpenChange={setFaPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="h-12 w-full justify-between font-normal"
                  >
                    {filters.fa && filters.fa.length > 0 
                      ? filters.fa.length === 1 
                        ? filters.fa[0].substring(0, 15) + (filters.fa[0].length > 15 ? '...' : '')
                        : `${filters.fa.length} FAs selected`
                      : "All FAs"}
                    <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search FAs..."
                      value={faSearchQuery}
                      onChange={(e) => setFaSearchQuery(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <ScrollArea className="h-[250px]">
                    <div className="p-2">
                      <div className="flex gap-2 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => { setFilters(prev => ({ ...prev, fa: [...filterOptions.fas] })); setCurrentPage(1); }}
                        >
                          Select All
                        </Button>
                        {filters.fa && filters.fa.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex-1 text-xs text-slate-500"
                            onClick={() => { setFilters(prev => ({ ...prev, fa: [] })); setCurrentPage(1); }}
                          >
                            Clear ({filters.fa.length})
                          </Button>
                        )}
                      </div>
                      {filterOptions.fas
                        .filter(fa => fa.toLowerCase().includes(faSearchQuery.toLowerCase()))
                        .map((fa) => (
                          <div
                            key={fa}
                            className="flex items-center space-x-2 py-1.5 px-2 hover:bg-slate-100 rounded cursor-pointer"
                            onClick={() => {
                              const newFas = filters.fa.includes(fa)
                                ? filters.fa.filter(f => f !== fa)
                                : [...filters.fa, fa];
                              setFilters(prev => ({ ...prev, fa: newFas }));
                              setCurrentPage(1);
                            }}
                          >
                            <Checkbox
                              checked={filters.fa.includes(fa)}
                              className="data-[state=checked]:bg-[#006994] data-[state=checked]:border-[#006994]"
                            />
                            <span className="text-sm truncate flex-1">{fa}</span>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>

            {/* DMA Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">DMA</Label>
              <MultiSelect
                options={filterOptions.mongoDmas}
                selected={filters.mongoDma}
                onChange={(val) => { setFilters(prev => ({ ...prev, mongoDma: val })); setCurrentPage(1); }}
                placeholder="All DMAs"
              />
            </div>

            {/* Stick Series Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Stick Series</Label>
              <MultiSelect
                options={filterOptions.stickSeriesList}
                selected={filters.stickSeries}
                onChange={(val) => { setFilters(prev => ({ ...prev, stickSeries: val })); setCurrentPage(1); }}
                placeholder="All Stick Series"
              />
            </div>

            <div className="relative">
              <Label className="text-xs text-slate-500 mb-1 block">City</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search city..."
                  value={citySearch}
                  onChange={(e) => {
                    setCitySearch(e.target.value);
                    setShowCityDropdown(true);
                    if (!e.target.value) {
                      handleFilterChange("city", "");
                    }
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  className="h-10 pl-9 pr-8"
                />
                {(citySearch || filters.city) && (
                  <button
                    onClick={() => {
                      setCitySearch("");
                      handleFilterChange("city", "");
                      setShowCityDropdown(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {showCityDropdown && citySearch && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-48 overflow-auto">
                  {filterOptions.cities
                    .filter(c => c.toLowerCase().includes(citySearch.toLowerCase()))
                    .slice(0, 15)
                    .map(city => (
                      <button
                        key={city}
                        onClick={() => {
                          handleFilterChange("city", city);
                          setCitySearch(city);
                          setShowCityDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0"
                      >
                        {city}
                      </button>
                    ))}
                  {filterOptions.cities.filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-sm text-slate-500">No cities found</div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Revenue</Label>
              <Select value={filters.revenueSort || "all"} onValueChange={(v) => handleFilterChange("revenueSort", v)}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Sort Revenue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">No Sort</SelectItem>
                  <SelectItem value="desc">
                    <span className="flex items-center gap-2"><ArrowDown className="w-3 h-3" /> Highest First</span>
                  </SelectItem>
                  <SelectItem value="asc">
                    <span className="flex items-center gap-2"><ArrowUp className="w-3 h-3" /> Lowest First</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-slate-100">
            <Label className="text-xs text-slate-500 mb-2 block font-medium">Offline Days Range</Label>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="2"
                  min="2"
                  value={filters.offlineDaysMin}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, offlineDaysMin: e.target.value }));
                    setCurrentPage(1);
                  }}
                  className="h-10 w-24"
                  data-testid="offline-days-min"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  min="2"
                  value={filters.offlineDaysMax}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, offlineDaysMax: e.target.value }));
                    setCurrentPage(1);
                  }}
                  className="h-10 w-24"
                  data-testid="offline-days-max"
                />
                <span className="text-sm text-slate-500">days</span>
              </div>
              {((filters.offlineDaysMin && filters.offlineDaysMin !== "2") || filters.offlineDaysMax) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, offlineDaysMin: "2", offlineDaysMax: "" }));
                    setCurrentPage(1);
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(165px,1fr))] gap-3 mb-8" data-testid="kpi-row">
          <KpiCard testId="kpi-date" label="Date" value={statsData.selectedDate || "-"} valueClass="text-slate-900" />
          <KpiCard testId="kpi-total-screens" label="Total Screens" value={statsData.totalScreens.toLocaleString()} valueClass="text-[#006994]" />
          <KpiCard testId="kpi-offline-screens" label="Offline Screens" value={statsData.totalOffline.toLocaleString()} pct={pctOf(statsData.totalOffline, statsData.totalScreens)} valueClass="text-slate-900" />
          <KpiCard testId="kpi-online-screens" label="Online Screens" value={statsData.onlineScreens.toLocaleString()} pct={pctOf(statsData.onlineScreens, statsData.totalScreens)} valueClass="text-green-600" />
          <KpiCard testId="kpi-states" label="States Affected" value={statsData.statesAffected.toLocaleString()} valueClass="text-slate-900" />
          <KpiCard testId="kpi-cities" label="Cities Affected" value={statsData.citiesAffected.toLocaleString()} valueClass="text-slate-900" />
          <KpiCard
            testId="kpi-revenue"
            label="Revenue Impact"
            sub="Last 2 months"
            value={`$${Math.round(statsData.totalRevenue).toLocaleString()}`}
            title={`$${statsData.totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
            valueClass="text-red-600"
          />
        </div>

        {/* Offline Device Map */}
        <div className="mb-8">
          <OfflineMap data={filteredData} mostRecentDateStr={filters.date || mostRecentDateStr} />
        </div>

        <div className="dolphin-card mb-8 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline Screens Table
            </h3>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold">Date</TableHead>
                  <TableHead className="text-xs font-semibold">DD Code</TableHead>
                  <TableHead className="text-xs font-semibold">Store Name</TableHead>
                  <TableHead className="text-xs font-semibold">Address</TableHead>
                  <TableHead className="text-xs font-semibold">City</TableHead>
                  <TableHead className="text-xs font-semibold">State</TableHead>
                  <TableHead className="text-xs font-semibold">PA</TableHead>
                  <TableHead className="text-xs font-semibold">FA</TableHead>
                  <TableHead className="text-xs font-semibold">Offline Days</TableHead>
                  <TableHead className="text-xs font-semibold">Revenue (Last 2 Months)</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Logs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12">
                      <div className="w-8 h-8 border-2 border-slate-200 border-t-[#006994] rounded-full animate-spin mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                      No offline screens found with current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, idx) => (
                    <TableRow key={idx} className="hover:bg-slate-50">
                      <TableCell className="text-sm">{row.Date}</TableCell>
                      <TableCell className="text-sm font-medium text-[#006994]">{row["Venue ID"]}</TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate">{row["Store Name"]}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={row.Address}>{row.Address}</TableCell>
                      <TableCell className="text-sm">{row.City}</TableCell>
                      <TableCell className="text-sm">{row.State}</TableCell>
                      <TableCell className="text-sm max-w-[120px] truncate">{row["PA Title"]}</TableCell>
                      <TableCell className="text-sm max-w-[120px] truncate">{row["FA Title"]}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {parseFloat(row["Offline Days"] || 0).toFixed(0)} days
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{formatRevenue(row["Last 2 Month Revenue"])}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openLogsForDD(row["Venue ID"])}
                          className="text-[#006994] hover:text-[#005A80] hover:bg-sky-50"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-3 text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="dolphin-card p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline Count by State (Top 10)
            </h3>
            <div className="h-[300px]" style={{ minHeight: "300px", minWidth: "200px" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={chartData.byState} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    formatter={(value) => [value, 'Offline Count']}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {chartData.byState.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dolphin-card p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline Count by City (Top 10)
            </h3>
            <div className="h-[300px]" style={{ minHeight: "300px", minWidth: "200px" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={chartData.byCity} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    formatter={(value) => [value, 'Offline Count']}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {chartData.byCity.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dolphin-card p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline Count by PA (Top 10)
            </h3>
            <div className="h-[300px]" style={{ minHeight: "300px", minWidth: "200px" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={chartData.byPA} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    formatter={(value, name, props) => [value, 'Offline Count']}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {chartData.byPA.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dolphin-card p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline by Network Type
            </h3>
            <div className="h-[300px]" style={{ minHeight: "300px", minWidth: "200px" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <PieChart>
                  <Pie
                    data={chartData.byNetwork}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {chartData.byNetwork.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    formatter={(value) => [value, 'Count']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="dolphin-card p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Offline Aging Distribution
                </h3>
                <p className="text-xs text-slate-500 mt-1">Duration breakdown of offline screens</p>
              </div>
            </div>
            <div className="h-[250px]" style={{ minHeight: "250px", minWidth: "200px" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={agingData} margin={{ bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    formatter={(value, name, props) => [value, props.payload.description]}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {agingData.map((entry, index) => {
                      const colors = ['#10B981', '#22C55E', '#EAB308', '#F97316', '#EF4444', '#DC2626', '#7F1D1D'];
                      return <Cell key={`cell-${index}`} fill={colors[index]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }}></div>
                <span className="text-slate-600">0-2 days: Newly offline</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22C55E' }}></div>
                <span className="text-slate-600">3-7 days: Short-term</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EAB308' }}></div>
                <span className="text-slate-600">8-30 days: Medium-term</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F97316' }}></div>
                <span className="text-slate-600">31-90 days: Long unresolved</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></div>
                <span className="text-slate-600">91-180 days: Critical</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#DC2626' }}></div>
                <span className="text-slate-600">181-300 days: Very old</span>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#7F1D1D' }}></div>
                <span className="text-slate-600">300+ days: Likely abandoned</span>
              </div>
            </div>
          </div>

          <div className="dolphin-card p-5" data-testid="offline-trend-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Offline Screens Trend
              </h3>
              <div className="flex rounded-lg bg-slate-100 p-1" data-testid="trend-range-filter">
                {[["7", "7D"], ["30", "30D"], ["90", "90D"], ["all", "All"]].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setTrendRange(val)}
                    data-testid={`trend-range-${val}`}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${trendRange === val ? "bg-white text-[#006994] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[300px] relative" style={{ minHeight: "250px", minWidth: "200px" }}>
              {trendLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70" data-testid="trend-loading">
                  <div className="animate-spin h-6 w-6 border-2 border-[#006994] border-t-transparent rounded-full" />
                </div>
              )}
              {!trendLoading && trendData.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400" data-testid="trend-empty">
                  No offline screens match the current filters.
                </div>
              )}
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <LineChart data={trendData} margin={{ top: 5, bottom: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }} 
                    interval={Math.max(0, Math.ceil(trendData.length / 8) - 1)}
                    minTickGap={12}
                  />
                  <YAxis tick={{ fontSize: 12 }} width={45} domain={["auto", "auto"]} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', padding: '12px' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length > 0) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-3 min-w-[180px]">
                            <p className="text-sm font-semibold text-slate-700 mb-2">
                              Date: {data.fullDate}
                            </p>
                            <p className="text-base font-bold text-[#006994] mb-2">
                              Offline Screens: {data.count}
                            </p>
                            {(data.newOffline > 0 || data.cameOnline > 0) && (
                              <div className="border-t border-slate-100 pt-2 mt-2 space-y-1">
                                {data.newOffline > 0 && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-red-500 font-bold">▲</span>
                                    <span className="text-sm text-slate-600">
                                      <span className="font-semibold text-red-600">{data.newOffline}</span> went offline
                                    </span>
                                  </div>
                                )}
                                {data.cameOnline > 0 && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-green-500 font-bold">▼</span>
                                    <span className="text-sm text-slate-600">
                                      <span className="font-semibold text-green-600">{data.cameOnline}</span> came online
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#006994" 
                    strokeWidth={2}
                    dot={trendData.length <= 31 ? { fill: '#006994', strokeWidth: 2, r: 3 } : false}
                    activeDot={{ r: 5, fill: '#006994' }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dolphin-card p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              OS Version Summary
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="w-40 text-left py-2 px-2 font-semibold text-slate-600">OS Version</th>
                    {/* <th className="text-right py-2 px-2 font-semibold text-slate-600">Total</th> */}
                    {/* <th className="text-right py-2 px-2 font-semibold text-slate-600">Online</th> */}
                    <th className="text-left py-2 px-2 font-semibold text-slate-600">Offline</th>
                    {/* <th className="text-right py-2 px-2 font-semibold text-slate-600">Offline %</th> */}
                  </tr>
                </thead>
                <tbody>
                  {osVersionSummary.map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2 font-medium text-slate-800">{row.os}</td>
                      {/* <td className="py-2 px-2 text-right text-slate-600">{row.total.toLocaleString()}</td> */}
                      {/* <td className="py-2 px-2 text-right text-green-600">{row.online.toLocaleString()}</td> */}
                      <td className="py-2 px-2 text-left text-red-600">{row.offline.toLocaleString()}</td>
                      {/* <td className="py-2 px-2 text-right">
                        <span className={`font-medium ${parseFloat(row.offlinePercent) > 50 ? 'text-red-600' : parseFloat(row.offlinePercent) > 30 ? 'text-orange-500' : 'text-slate-600'}`}>
                          {row.offlinePercent}%
                        </span>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="dolphin-card p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline by OS Version
            </h3>
            <div className="h-[300px]" style={{ minHeight: "300px", minWidth: "200px" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={chartData.byOS} margin={{ bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    formatter={(value) => [value, 'Offline Count']}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {chartData.byOS.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#006994]">
              <Mail className="w-5 h-5" />
              Email Report
            </DialogTitle>
            <DialogDescription>
              Send the filtered analytics report to an email address.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Filter Summary */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Report Filters</p>
              <p className="text-sm text-slate-700">{getFilterSummary()}</p>
            </div>

            {/* Recipient Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Recipient Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="h-11"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Include Attachments</Label>
              <div className="flex flex-col gap-3">
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                  <Checkbox 
                    id="include-pdf" 
                    checked={includePdf} 
                    onCheckedChange={setIncludePdf}
                    className="data-[state=checked]:bg-[#006994] data-[state=checked]:border-[#006994]"
                  />
                  <div className="flex-1">
                    <label htmlFor="include-pdf" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <FileText className="w-4 h-4 text-red-500" />
                      PDF Report
                    </label>
                    <p className="text-xs text-slate-500">Summary statistics and charts</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                  <Checkbox 
                    id="include-csv" 
                    checked={includeCsv} 
                    onCheckedChange={setIncludeCsv}
                    className="data-[state=checked]:bg-[#006994] data-[state=checked]:border-[#006994]"
                  />
                  <div className="flex-1">
                    <label htmlFor="include-csv" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <Download className="w-4 h-4 text-green-500" />
                      CSV Data
                    </label>
                    <p className="text-xs text-slate-500">Full filtered data ({analyticsData.length} records)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEmailModalOpen(false)} disabled={sendingEmail}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail} 
              disabled={sendingEmail || !recipientEmail || (!includePdf && !includeCsv)}
              className="bg-[#006994] hover:bg-[#005577]"
            >
              {sendingEmail ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
